import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiRequestError, AuthError } from '../services/api/client';
import { quotesApi } from '../services/api/quotes';
import { isEmail, isPositiveNumber, required } from '../utils/validators';
import { quoteKeys } from './quoteKeys';
import { useUser } from './useUser';
import type { User } from '../types/auth';
import { legalLocaleForUiLocale, normalizeUiLocale, TERMS_DOCUMENT_VERSION } from '../legal/documents';
import { isTermsVersionStale } from '../legal/acceptance';
import { authApi } from '../services/api/auth';
import { api } from '../services/api/client';

export interface FormDataState {
  origin_name: string;
  origin_country: string;
  origin_zip_code: string;
  origin_city: string;
  origin_street: string;

  destination_name: string;
  destination_country: string;
  destination_zip_code: string;
  destination_city: string;
  destination_street: string;

  date_type: 'fixed' | 'flexible' | 'none';

  origin_fixed_date: string;
  destination_fixed_date: string;
  origin_fixed_hour: string;
  destination_fixed_hour: string;

  origin_date: string;
  destination_date: string;

  cargo_title: string;
  cargo_length: string;
  cargo_width: string;
  cargo_height: string;
  cargo_weight: string;

  transportation_adr: boolean;
  transportation_closed_truck: boolean;
  transportation_crane_truck: boolean;
  transportation_aog: boolean;
  transportation_tarpaulin: boolean;

  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_company: string;

  observations: string;
  [key: string]: string | boolean;
}

export interface CreateQuoteFieldErrors {
  route: {
    origin_country: string[];
    origin_zip_code: string[];
    origin_city: string[];
    destination_country: string[];
    destination_zip_code: string[];
    destination_city: string[];
  };
  cargo: {
    cargo_title: string[];
    cargo_length: string[];
    cargo_width: string[];
    cargo_height: string[];
    cargo_weight: string[];
  };
  contact: {
    contact_name: string[];
    contact_email: string[];
  };
  cargo_images: string[];
}

const createEmptyFieldErrors = (): CreateQuoteFieldErrors => ({
  route: {
    origin_country: [],
    origin_zip_code: [],
    origin_city: [],
    destination_country: [],
    destination_zip_code: [],
    destination_city: [],
  },
  cargo: {
    cargo_title: [],
    cargo_length: [],
    cargo_width: [],
    cargo_height: [],
    cargo_weight: [],
  },
  contact: {
    contact_name: [],
    contact_email: [],
  },
  cargo_images: [],
});
const initialFormData: FormDataState = {
  origin_name: '',
  origin_country: '',
  origin_zip_code: '',
  origin_city: '',
  origin_street: '',
  destination_name: '',
  destination_country: '',
  destination_zip_code: '',
  destination_city: '',
  destination_street: '',
  date_type: 'fixed',
  origin_fixed_date: '',
  destination_fixed_date: '',
  origin_fixed_hour: '',
  destination_fixed_hour: '',
  delivery_hour: '',
  origin_date: '',
  destination_date: '',
  cargo_title: '',
  cargo_length: '',
  cargo_width: '',
  cargo_height: '',
  cargo_weight: '',
  transportation_adr: false,
  transportation_closed_truck: false,
  transportation_crane_truck: false,
  transportation_aog: false,
  transportation_tarpaulin: false,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact_company: '',
  observations: ''
};

const buildInitialFormData = (user: User | null): FormDataState => ({
  ...initialFormData,
  contact_name: user?.name ?? '',
  contact_email: user?.email_address ?? '',
});

// Owner-approved quote-request attachment limits (issue #279). These mirror
// the authoritative backend validation; they exist here to give the requester
// immediate feedback before a request is sent. The requester asked for a
// 10 MB per-file ceiling with up to 10 files; the combined guard stays at
// 100 MiB (10 x 10 MiB), matching the upstream aggregate cap.
export const MAX_CARGO_FILES = 10;
const MAX_CARGO_FILE_BYTES = 10 * 1024 * 1024;
const MAX_CARGO_TOTAL_BYTES = 100 * 1024 * 1024;
// Display labels used for the localized limits note and validation messages.
export const MAX_CARGO_FILE_SIZE_LABEL = '10 MB';
export const MAX_CARGO_TOTAL_SIZE_LABEL = '100 MB';

const ALLOWED_CARGO_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

// Collects the distinct, localized validation keys for a candidate attachment
// set: unsupported type, per-file size, combined size and count. A single
// rejected batch yields one message per violated rule so the requester sees
// exactly what to fix.
const validateFiles = (fileList: File[]): string[] => {
  const errors: string[] = [];
  let totalBytes = 0;
  let hasInvalidType = false;
  let hasOversizedFile = false;

  fileList.forEach((file) => {
    totalBytes += file.size;

    if (!ALLOWED_CARGO_IMAGE_TYPES.includes(file.type)) {
      hasInvalidType = true;
    }

    if (file.size > MAX_CARGO_FILE_BYTES) {
      hasOversizedFile = true;
    }
  });

  if (fileList.length > MAX_CARGO_FILES) {
    errors.push('quote.form.validation.too_many_files');
  }

  if (hasInvalidType) {
    errors.push('quote.form.validation.invalid_file_type');
  }

  if (hasOversizedFile) {
    errors.push('quote.form.validation.file_too_large');
  }

  // Only surface the combined-size message when no single file is already over
  // the per-file ceiling — that per-file message is the actionable one.
  if (!hasOversizedFile && totalBytes > MAX_CARGO_TOTAL_BYTES) {
    errors.push('quote.form.validation.files_too_large');
  }

  return errors;
};

export const useCreateQuote = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const { user, clearUser } = useUser();
  const token = api.getToken();
  const termsStatus = useQuery({
    queryKey: ['auth', 'current-user', token],
    queryFn: async () => {
      try {
        return await authApi.currentUser();
      } catch (error) {
        // The auth status check is optional on this public form. Handle an
        // expired session here so the global auth-query handler cannot redirect
        // away from a quote with unsaved fields and attachments.
        if (error instanceof AuthError) {
          api.removeToken();
          clearUser();
          return null;
        }

        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const termsAlreadyAccepted = Boolean(token && termsStatus.isSuccess && termsStatus.data?.user?.terms_acceptance_required === false);
  const [formData, setFormData] = useState<FormDataState>(() => buildInitialFormData(user));
  const [files, setFiles] = useState<File[]>([]);
  const [fieldErrors, setFieldErrors] = useState<CreateQuoteFieldErrors>(createEmptyFieldErrors());
  const [formErrors, setFormErrors] = useState<string[]>([]);
  // Errors from a rejected attachment batch. Non-null means a batch was
  // rejected and is still blocking submission until it is resolved by a valid
  // selection or explicitly dismissed.
  const [rejectedFileErrors, setRejectedFileErrors] = useState<string[] | null>(null);

  // Terms acceptance is a required, initially-unchecked control on the
  // anonymous quote path. The recorded language is the language the legal
  // document is actually published in (Spanish interfaces read the English
  // document). It never requires an account and never bundles marketing.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsStale, setTermsStale] = useState(false);
  const [termsError, setTermsError] = useState('');
  const termsDocumentLanguage = legalLocaleForUiLocale(
    normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language),
  );

  const validate = () => {
    const nextFieldErrors = createEmptyFieldErrors();
    const nextFormErrors: string[] = [];
    let requiredFieldCount = 0;
    let termsErrorKey = '';

    const validateRequiredField = (value: string, errors: string[], errorKey = 'quote.form.validation.required') => {
      const error = required(value, errorKey);
      if (error) {
        errors.push(error);
        requiredFieldCount += 1;
      }
    };

    const validateEmailField = (value: string, errors: string[]) => {
      validateRequiredField(value, errors);

      if (errors.length === 0) {
        const error = isEmail(value, 'quote.form.validation.invalid_email');
        if (error) {
          errors.push(error);
          nextFormErrors.push(error);
        }
      }
    };

    const validatePositiveField = (value: string, errors: string[]) => {
      const requiredError = required(value, 'quote.form.validation.required');
      if (requiredError) {
        errors.push(requiredError);
        requiredFieldCount += 1;
        return;
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        errors.push('quote.form.validation.invalid_number');
        nextFormErrors.push('quote.form.validation.invalid_number');
        return;
      }

      const positiveError = isPositiveNumber(numericValue, 'quote.form.validation.positive_number');
      if (positiveError) {
        errors.push(positiveError);
        nextFormErrors.push(positiveError);
      }
    };

    validateRequiredField(formData.origin_country, nextFieldErrors.route.origin_country);
    validateRequiredField(formData.origin_zip_code, nextFieldErrors.route.origin_zip_code);
    validateRequiredField(formData.origin_city, nextFieldErrors.route.origin_city);
    validateRequiredField(formData.destination_country, nextFieldErrors.route.destination_country);
    validateRequiredField(formData.destination_zip_code, nextFieldErrors.route.destination_zip_code);
    validateRequiredField(formData.destination_city, nextFieldErrors.route.destination_city);

    validateRequiredField(formData.cargo_title, nextFieldErrors.cargo.cargo_title);
    validatePositiveField(formData.cargo_length, nextFieldErrors.cargo.cargo_length);
    validatePositiveField(formData.cargo_width, nextFieldErrors.cargo.cargo_width);
    validatePositiveField(formData.cargo_height, nextFieldErrors.cargo.cargo_height);
    validatePositiveField(formData.cargo_weight, nextFieldErrors.cargo.cargo_weight);

    validateRequiredField(formData.contact_name, nextFieldErrors.contact.contact_name);
    validateEmailField(formData.contact_email, nextFieldErrors.contact.contact_email);

    // A rejected attachment batch stays visible and keeps blocking submission
    // until the requester either makes a valid new selection or explicitly
    // continues. validate() must never silently clear it.
    const attachmentErrors = validateFiles(files);
    const cargoImageErrors = rejectedFileErrors ?? attachmentErrors;
    nextFieldErrors.cargo_images.push(...cargoImageErrors);

    // Terms acceptance is an independent gate: a submission needs both a valid
    // attachment set and an affirmative acceptance. Neither bypasses the other.
    if (!termsAlreadyAccepted && !termsAccepted) {
      termsErrorKey = 'legal.terms_acceptance.required';
    }

    if (requiredFieldCount === 1) {
      nextFormErrors.push('quote.form.validation.required_field_singular');
    } else if (requiredFieldCount > 1) {
      nextFormErrors.push('quote.form.validation.required_fields_plural');
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);
    setTermsError(termsErrorKey);

    // All independent gates must hold: a valid attachment set (no pending
    // rejected batch) and an affirmative Terms acceptance. Neither bypasses the
    // other. Attachment errors are shown beside
    // the file controls; they still block submission even though they are not
    // mirrored into the summary banner.
    return (
      nextFormErrors.length === 0 &&
      termsErrorKey === '' &&
      cargoImageErrors.length === 0 &&
      rejectedFileErrors === null
    );
  };

  const mutation = useMutation({
    mutationFn: () => {
      const data = new FormData();

      Object.keys(formData).forEach(key => {
        if (formData.date_type === 'fixed') {
          if (['origin_date', 'destination_date'].includes(key)) return;
        } else if (formData.date_type === 'flexible') {
          if (['origin_fixed_date', 'destination_fixed_date', 'origin_fixed_hour', 'destination_fixed_hour', 'delivery_hour'].includes(key)) return;
        } else if (formData.date_type === 'none') {
           if (['origin_fixed_date', 'destination_fixed_date', 'origin_fixed_hour', 'destination_fixed_hour', 'origin_date', 'destination_date', 'delivery_hour'].includes(key)) return;
        }

        data.append(`cargo_quote_request[${key}]`, String(formData[key]));
      });

      files.forEach(file => {
        data.append('cargo_quote_request[cargo_images][]', file);
      });

      // Terms acceptance evidence travels with the request root alongside the
      // quote fields: affirmative flag, immutable document version, and the
      // language the legal document is published in.
      if (termsAccepted) data.append('cargo_quote_request[terms_accepted]', 'true');
      data.append('cargo_quote_request[terms_version]', TERMS_DOCUMENT_VERSION);
      data.append('cargo_quote_request[terms_document_language]', termsDocumentLanguage);

      return quotesApi.createQuote(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      if (token) queryClient.invalidateQueries({ queryKey: ['auth', 'current-user', token] });
      setFieldErrors(createEmptyFieldErrors());
      setFormErrors([]);
      toast.success(t('quote.form.toasts.success'));
      navigate('/success', {
        state: {
          trackingNumber: response.cargo_quote_request.tracking_number,
          email: formData.contact_email
        }
      });
      window.scrollTo(0, 0);
    },
    onError: (err: unknown) => {
      // A stale document version must not be accepted automatically. Keep the
      // entered data, surface the localized prompt beside the checkbox and
      // require the visitor to tick the box again after re-reading.
      if (isTermsVersionStale(err)) {
        setTermsAccepted(false);
        setTermsStale(true);
        setTermsError('');
        if (token) queryClient.setQueryData(['auth', 'current-user', token], (current: { user?: User } | undefined) => current ? { ...current, user: { ...current.user, terms_acceptance_required: true } } : current);
        setFormErrors([]);
        toast.error(t('legal.terms_acceptance.stale_notice'));
        return;
      }

      if (err instanceof ApiRequestError && err.status === 422 && err.errors?.includes('terms_acceptance_required')) {
        setTermsAccepted(false);
        setTermsStale(false);
        setTermsError('legal.terms_acceptance.required');
        setFormErrors([]);
        if (token) queryClient.setQueryData(['auth', 'current-user', token], (current: { user?: User } | undefined) => current ? { ...current, user: { ...current.user, terms_acceptance_required: true } } : current);
        return;
      }

      // A 413 from the proxy means the payload was too large to send. The
      // proxy answers with HTML, so there is no server error array to show;
      // use a localized, actionable upload message instead.
      if (err instanceof ApiRequestError && err.status === 413) {
        const message = t('quote.form.validation.upload_too_large', {
          maxFiles: MAX_CARGO_FILES,
          maxSize: MAX_CARGO_FILE_SIZE_LABEL,
          maxTotal: MAX_CARGO_TOTAL_SIZE_LABEL,
        });
        setFormErrors([message]);
        toast.error(message);
        return;
      }

      if (err instanceof ApiRequestError && err.errors && err.errors.length > 0) {
        setFormErrors(err.errors);
      } else {
        setFormErrors([(err as Error).message]);
      }
      toast.error(
        err instanceof Error ? err.message : t('quote.form.toasts.create_failed')
      );
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCountryChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const incoming = Array.from(e.target.files);

    // Clear the native input so re-picking the same file after removing its
    // chip still fires a change event (the browser only fires a change when the
    // input's value actually changes). The reset happens whether the batch is
    // accepted or rejected.
    e.target.value = '';

    // Validate the combined set before accepting anything: an attachment set
    // must satisfy type/per-file/combined/count limits as a whole.
    const combined = [...files, ...incoming];
    const candidateErrors = validateFiles(combined);

    if (candidateErrors.length > 0) {
      // Reject the whole new batch: keep the previously accepted files and the
      // scalar form input untouched, and record the rejection so it stays
      // visible and keeps blocking submission until it is resolved by a valid
      // selection or explicitly dismissed.
      setRejectedFileErrors(candidateErrors);
      setFieldErrors((prev) => ({ ...prev, cargo_images: candidateErrors }));
      return;
    }

    // A valid combined set clears any previous rejection.
    setFiles(combined);
    setRejectedFileErrors(null);
    setFieldErrors((prev) => ({ ...prev, cargo_images: [] }));
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
    // Removing an accepted file does not resolve a rejected batch (the batch is
    // not stored), so the rejection stays visible until it is resolved or
    // explicitly dismissed.
    setFieldErrors((prev) => ({
      ...prev,
      cargo_images: rejectedFileErrors ?? [],
    }));
  };

  const handleDismissRejectedFiles = () => {
    // Explicit acknowledgement: drop the rejected batch from the conversation
    // and continue with whatever was already accepted.
    setRejectedFileErrors(null);
    setFieldErrors((prev) => ({ ...prev, cargo_images: [] }));
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setTermsAccepted(checked);

    if (checked) {
      setTermsError('');
      setTermsStale(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setFormErrors([]);
    mutation.mutate();
  };

  return {
    formData, setFormData,
    fieldErrors,
    formErrors,
    validate,
    handleChange, handleCountryChange, handleFileChange, handleRemoveFile,
    handleDismissRejectedFiles, handleSubmit,
    termsAccepted, termsError, termsStale, termsAlreadyAccepted, handleTermsChange,
    files, hasRejectedFiles: rejectedFileErrors !== null,
    loading: mutation.isPending,
    errors: formErrors,
  };
};
